<template>
  <div class="posts-feed">
    <!-- Post Composer (Admin/Coach only) -->
    <PostComposer
      v-if="canCreatePost"
      :user-data="userData"
      :team-id="teamId"
      :club-id="clubId"
      :team-name="teamName"
      @post-created="handlePostCreated"
    />

    <!-- Loading State -->
    <div v-if="loading && posts.length === 0" class="loading-state">
      <div class="spinner"></div>
      <p>Loading posts...</p>
    </div>

    <!-- Posts List + sort dropdown -->
    <template v-else-if="posts.length > 0">
      <div class="posts-feed-toolbar">
        <label class="posts-feed-sort-label">Sort by</label>
        <select v-model="sortBy" class="posts-feed-sort">
          <option value="newest">Newest</option>
          <option value="reactions">Most reactions</option>
          <option value="comments">Most comments</option>
          <option value="pinned">Pinned first</option>
        </select>
      </div>

      <div class="posts-list">
        <PostCard
          v-for="post in sortedPosts"
          :key="post.id"
          :post="post"
          :user-data="userData"
          :can-moderate="canCreatePost"
          @post-updated="handlePostUpdated"
          @post-deleted="handlePostDeleted"
          @reaction-toggled="handleReactionToggled"
        />

        <!-- Load More -->
        <div v-if="hasMore" class="load-more-wrapper">
          <button
            class="load-more-btn"
            :disabled="loadingMore"
            @click="loadMorePosts"
          >
            {{ loadingMore ? 'Loading...' : 'Load More Posts' }}
          </button>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" width="64" height="64" fill="#bcc0c4">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v2h-2v-2zm0-8h2v6h-2V9z"/>
        </svg>
      </div>
      <h3>No posts yet</h3>
      <p v-if="canCreatePost">Be the first to share something with your team!</p>
      <p v-else>Check back later for updates from your coaches.</p>
    </div>
  </div>
</template>

<script>
import { TeamPosts } from "@/services/team-posts-service";
import PostComposer from "./PostComposer.vue";
import PostCard from "./PostCard.vue";

export default {
  name: "PostsFeed",
  components: {
    PostComposer,
    PostCard,
  },
  props: {
    teamId: {
      type: [Number, String],
      required: true,
    },
    clubId: {
      type: [Number, String],
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    teamName: {
      type: String,
      default: "",
    },
    canCreatePost: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      posts: [],
      loading: false,
      loadingMore: false,
      page: 1,
      limit: 10,
      hasMore: false,
      sortBy: "newest",
    };
  },
  computed: {
    sortedPosts() {
      const list = [...this.posts];
      const reactionCount = (p) => {
        if (!p.reactions) return 0;
        if (Array.isArray(p.reactions)) return p.reactions.length;
        return Object.values(p.reactions).reduce(
          (s, v) => s + (Array.isArray(v) ? v.length : Number(v) || 0),
          0,
        );
      };
      const commentCount = (p) => Number(p.comment_count || p.comments_count || 0);
      const ts = (p) => new Date(p.created_at || p.created_date || 0).getTime();

      if (this.sortBy === "reactions") {
        list.sort((a, b) => reactionCount(b) - reactionCount(a) || ts(b) - ts(a));
      } else if (this.sortBy === "comments") {
        list.sort((a, b) => commentCount(b) - commentCount(a) || ts(b) - ts(a));
      } else if (this.sortBy === "pinned") {
        list.sort((a, b) => {
          const ap = a.is_pinned ? 1 : 0;
          const bp = b.is_pinned ? 1 : 0;
          if (ap !== bp) return bp - ap;
          return ts(b) - ts(a);
        });
      } else {
        list.sort((a, b) => ts(b) - ts(a));
      }
      return list;
    },
  },
  watch: {
    teamId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.resetAndLoadPosts();
        }
      },
    },
  },
  methods: {
    async resetAndLoadPosts() {
      this.posts = [];
      this.page = 1;
      this.hasMore = false;
      await this.loadPosts();
    },

    async loadPosts() {
      this.loading = true;
      try {
        const response = await TeamPosts.getPosts(
          this.teamId,
          this.page,
          this.limit
        );

        if (response.data.success) {
          this.posts = [...response.data.posts];
          this.hasMore = response.data.pagination.hasMore;
        }
      } catch (error) {
        console.error("Error loading posts:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadMorePosts() {
      this.loadingMore = true;
      this.page++;

      try {
        const response = await TeamPosts.getPosts(
          this.teamId,
          this.page,
          this.limit
        );

        if (response.data.success) {
          this.posts = [...this.posts, ...response.data.posts];
          this.hasMore = response.data.pagination.hasMore;
        }
      } catch (error) {
        console.error("Error loading more posts:", error);
        this.page--;
      } finally {
        this.loadingMore = false;
      }
    },

    handlePostCreated(newPost) {
      // Add new post to the top of the list
      this.posts = [newPost, ...this.posts];
    },

    handlePostUpdated(updatedPost) {
      const index = this.posts.findIndex((p) => p.id === updatedPost.id);
      if (index !== -1) {
        this.posts = [
          ...this.posts.slice(0, index),
          updatedPost,
          ...this.posts.slice(index + 1),
        ];
      }
    },

    handlePostDeleted(postId) {
      this.posts = this.posts.filter((p) => p.id !== postId);
    },

    handleReactionToggled({ postId, reactions }) {
      const index = this.posts.findIndex((p) => p.id === postId);
      if (index !== -1) {
        const updatedPost = { ...this.posts[index], reactions };
        this.posts = [
          ...this.posts.slice(0, index),
          updatedPost,
          ...this.posts.slice(index + 1),
        ];
      }
    },
  },
};
</script>

<style scoped>
.posts-feed {
  max-width: 680px;
  margin: 0 auto;
}

.posts-feed-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 12px;
}

.posts-feed-sort-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin: 0;
}

.posts-feed-sort {
  height: 32px;
  padding: 0 1.75rem 0 0.6rem;
  font-size: 0.85rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.posts-feed-sort:focus {
  outline: 0;
  border-color: #80bdff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
}

.posts-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  color: #65676b;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e4e6eb;
  border-top-color: #1877f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 16px;
  text-align: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 8px;
  color: #1c1e21;
  font-size: 20px;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  color: #65676b;
  font-size: 15px;
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.load-more-btn {
  padding: 10px 24px;
  background: #e4e6eb;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #050505;
  cursor: pointer;
  transition: background 0.2s ease;
}

.load-more-btn:hover:not(:disabled) {
  background: #d8dadf;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .posts-feed {
    max-width: 100%;
  }

  .empty-state {
    padding: 48px 16px;
  }
}
</style>
